<script setup>

import {ref} from "vue";
import { useForm } from "@inertiajs/vue3";
import LIVR from 'livr';
import TextInput from '@/Components/TextInput.vue';
import InputLabel from '@/Components/InputLabel.vue';
import InputError from '@/Components/InputError.vue';
import TextArea from "@/Components/TextArea.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import ReviewRating from "@/Components/Product/ReviewRating.vue";

const sent = ref(false);

const props = defineProps({
  product : {
    type: Object,
    required: true
  }
})

LIVR.Validator.defaultAutoTrim(true);

const form = useForm({
	nickname: null,
	title: null,
	detail: null,
	rating: 5,
	processing: false
});

const validatorRules = {
	nickname: ['required', {min_length: 2}],
	title: ['required', {min_length: 2}],
	detail: ['required', {min_length: 10}],
	rating: ['required', {min_number: 0, max_number: 5}]
};

let validator = new LIVR.Validator(validatorRules);

const validateForm = function () {
	const validation = {
		isValid: validator.validate(form.data()),
		errors: validator.getErrors()
	};
	return validation;
}

const inputBlurred = function (event) {
	const errors = validateForm().errors;
	if (errors?.hasOwnProperty(event.target.id)) {
		form.setError(event.target.id, errors[event.target.id]);
	} else {
		form.clearErrors(event.target.id);
	}
}

const submit = () => {

	let validation = validateForm();

  if (validation.isValid) {
		form.processing = true;

    axios.post(route('review.store', props.product.uuid), form.data())
    .then(response => {
      form.processing = false;
      form.reset();
      sent.value = true;
    })
    .catch(error => {
      form.processing = false;
      console.log(error);
    });

	} else {
		for (const [key, value] of Object.entries(validation.errors)) {
			form.setError(key, value);
		}
	}



};

</script>

<template>

  <div id="new-review-container" class="col-12 new-review-container" :class="{
    'col-lg-6': product.reviews.length,
  }">

    <div class="header">
      <span class="title">{{__('your_opinion_matters')}}</span>
    </div>

    <div class="position-relative" style="min-height: 400px; max-width: 700px;">

      <div v-if="!sent">
        <form @submit.prevent="submit" class="w-100" id="review_form" v-if="!form.processing">

          <div class="d-flex justify-content-between gap-3">
            <div class="mt-3 flex-grow-1">
              <InputLabel for="title">{{__('review_title')}}</InputLabel>
              <TextInput class="mt-1" id="title" type="text" v-model="form.title" @blur="inputBlurred($event)" />
              <InputError class="mt-2 mb-4" v-if="form.errors.title" :message="__('form.' + form.errors.title)" />
            </div>
            <div class="mt-3">
              <InputLabel for="rating">{{__('review_levels.' + form.rating)}}</InputLabel>
              <ReviewRating v-model="form.rating" class="mt-3" />
              <InputError class="mt-2 mb-4" v-if="form.errors.rating" :message="__('form.' + form.errors.rating)" />
            </div>

          </div>

          <div class="mt-3">
            <InputLabel for="detail">{{__('description')}}</InputLabel>
            <TextArea id="detail" v-model="form.detail" @blur="inputBlurred($event)" />
            <InputError class="mt-2 mb-4" v-if="form.errors.detail" :message="__('form.' + form.errors.detail)" />
          </div>



          <div class="mt-3">
            <InputLabel for="nickname">{{__('form.nickname')}}</InputLabel>
            <TextInput class="mt-1" id="nickname" type="text" v-model="form.nickname" @blur="inputBlurred($event)" />
            <InputError class="mt-2 mb-4" v-if="form.errors.nickname" :message="__('form.' + form.errors.nickname)" />
          </div>

          <div class="mt-3">
            <PrimaryButton type="submit" :loading="form.processing">{{__('submit_review')}}</PrimaryButton>
          </div>
        </form>

        <div class="spinner-wrapper position-absolute w-100 h-100 d-flex justify-content-center align-items-center" v-else="">
          <div class="spinner">
            <div class="bounce1"></div>
            <div class="bounce2"></div>
            <div class="bounce3"></div>
          </div>
        </div>
      </div>

      <div v-else>
        <div class="alert alert-success d-flex flex-column align-items-center justify-content-center" style="min-height: 300px;" role="alert">
          <h2 class="font-semibold text-success">{{__('review_sent')}}</h2>
          <p>{{__('review_sent_message')}}</p>
        </div>
      </div>


    </div>

  </div>

</template>

<style scoped>

</style>